// Serialization for user-folder artifacts (paths, polygons, placemarks,
// measurements). KML / GeoJSON / GPX are generated client-side from the
// artifact's inline geometryJson — no round-trip. Shapefile / SVG / DXF
// need server-side conversion and route through the ExportUserArtifact
// RPC (artifactExportService.exportUserArtifact); on success the server
// returns a short-TTL presigned URL that the browser fetches directly.
import { artifactExportService, ExportFormat } from '../services/artifactExportService';
const sanitizeFilename = (name) => name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'artifact';
// KML colour is AABBGGRR hex (note: opposite byte order from CSS RGB and
// alpha first). Default opacity ff. Falls back to opaque white if the input
// can't be parsed — keeps export non-fatal on unexpected input.
const cssToKmlColor = (css) => {
    const m = /^#([0-9a-fA-F]{6})$/.exec(css.trim());
    if (!m)
        return 'ffffffff';
    const rr = m[1].slice(0, 2);
    const gg = m[1].slice(2, 4);
    const bb = m[1].slice(4, 6);
    return `ff${bb}${gg}${rr}`.toLowerCase();
};
const escapeXml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
const readPrimitive = (artifact) => {
    let g;
    try {
        g = JSON.parse(artifact.geometryJson);
    }
    catch (_a) {
        return null;
    }
    switch (artifact.artifactType) {
        case 'placemark':
            if (typeof g.lat !== 'number' || typeof g.lng !== 'number')
                return null;
            return { kind: 'point', coord: { lng: g.lng, lat: g.lat } };
        case 'measurement':
            if (typeof g.startLat !== 'number' || typeof g.startLng !== 'number'
                || typeof g.endLat !== 'number' || typeof g.endLng !== 'number')
                return null;
            return { kind: 'linestring', coords: [
                    { lng: g.startLng, lat: g.startLat },
                    { lng: g.endLng, lat: g.endLat },
                ] };
        case 'measurement_circle': {
            if (typeof g.centerLat !== 'number' || typeof g.centerLng !== 'number'
                || typeof g.radiusMeters !== 'number')
                return null;
            // Approximate circle as a 32-segment closed polygon ring. Uses planar
            // (equirectangular) offset which is sufficient for radii up to a few
            // kilometres at most latitudes; matches Cesium ellipse rendering.
            const segments = 32;
            const earthR = 6378137; // meters
            const latRad = g.centerLat * Math.PI / 180;
            const ring = [];
            for (let i = 0; i <= segments; i++) {
                const t = (i / segments) * 2 * Math.PI;
                const dLat = (g.radiusMeters * Math.cos(t)) / earthR;
                const dLng = (g.radiusMeters * Math.sin(t)) / (earthR * Math.cos(latRad));
                ring.push({
                    lng: g.centerLng + dLng * 180 / Math.PI,
                    lat: g.centerLat + dLat * 180 / Math.PI,
                });
            }
            return { kind: 'polygon', ring };
        }
        case 'path': {
            const points = Array.isArray(g.points) ? g.points : null;
            if (!points || points.length < 2)
                return null;
            return { kind: 'linestring', coords: points.map((p) => ({ lng: p.lng, lat: p.lat })) };
        }
        case 'polygon': {
            const verts = Array.isArray(g.vertices) ? g.vertices : null;
            if (!verts || verts.length < 3)
                return null;
            // KML/GeoJSON polygon rings must be explicitly closed.
            const ring = verts.map((v) => ({ lng: v.lng, lat: v.lat }));
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first.lng !== last.lng || first.lat !== last.lat)
                ring.push({ ...first });
            return { kind: 'polygon', ring };
        }
        default:
            return null;
    }
};
const buildGeoJSONFeature = (artifact, p) => {
    const properties = {
        name: artifact.name,
        color: artifact.color,
        type: artifact.artifactType,
    };
    if (artifact.icon)
        properties.icon = artifact.icon;
    if (artifact.showLabel !== undefined)
        properties.showLabel = artifact.showLabel;
    let geometry;
    switch (p.kind) {
        case 'point':
            geometry = { type: 'Point', coordinates: [p.coord.lng, p.coord.lat] };
            break;
        case 'linestring':
            geometry = { type: 'LineString', coordinates: p.coords.map(c => [c.lng, c.lat]) };
            break;
        case 'polygon':
            geometry = { type: 'Polygon', coordinates: [p.ring.map(c => [c.lng, c.lat])] };
            break;
    }
    return { type: 'Feature', properties, geometry };
};
const toGeoJSON = (artifact, p) => JSON.stringify({
    type: 'FeatureCollection',
    features: [buildGeoJSONFeature(artifact, p)],
}, null, 2);
// Builds the <Style> + <Placemark> pair for one artifact. styleId must be
// unique within the enclosing <Document> so multiple features can coexist
// (folder export) without clobbering each other's colours.
const buildKmlPlacemark = (artifact, p, styleId) => {
    const colour = cssToKmlColor(artifact.color);
    const lineStyle = `<LineStyle><color>${colour}</color><width>3</width></LineStyle>`;
    const polyStyle = `<PolyStyle><color>7f${colour.slice(2)}</color></PolyStyle>`;
    const iconStyle = `<IconStyle><color>${colour}</color></IconStyle>`;
    const fmtCoord = (c) => `${c.lng},${c.lat},0`;
    let body = '';
    switch (p.kind) {
        case 'point':
            body = `<Point><coordinates>${fmtCoord(p.coord)}</coordinates></Point>`;
            break;
        case 'linestring':
            body = `<LineString><tessellate>1</tessellate><coordinates>${p.coords.map(fmtCoord).join(' ')}</coordinates></LineString>`;
            break;
        case 'polygon':
            body = `<Polygon><outerBoundaryIs><LinearRing><coordinates>${p.ring.map(fmtCoord).join(' ')}</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
            break;
    }
    const extendedFields = [];
    if (artifact.icon) {
        extendedFields.push(`<Data name="icon"><value>${escapeXml(artifact.icon)}</value></Data>`);
    }
    if (artifact.showLabel !== undefined) {
        extendedFields.push(`<Data name="show_label"><value>${artifact.showLabel ? 'true' : 'false'}</value></Data>`);
    }
    const extendedData = extendedFields.length > 0
        ? `<ExtendedData>${extendedFields.join('')}</ExtendedData>`
        : '';
    const style = `<Style id="${styleId}">${iconStyle}${lineStyle}${polyStyle}</Style>`;
    const placemark = `<Placemark><name>${escapeXml(artifact.name)}</name><styleUrl>#${styleId}</styleUrl>${extendedData}${body}</Placemark>`;
    return { style, placemark };
};
const toKML = (artifact, p) => {
    const { style, placemark } = buildKmlPlacemark(artifact, p, 'sty');
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(artifact.name)}</name>
    ${style}
    ${placemark}
  </Document>
</kml>
`;
};
const GPX_HEADER = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="LandScope" xmlns="http://www.topografix.com/GPX/1/1">
`;
const GPX_FOOTER = `</gpx>
`;
// GPX 1.1: places (placemarks) become <wpt>, lines (measurements + paths)
// become <trk><trkseg><trkpt>. Polygons have no GPX equivalent — returns
// null so callers can skip them (single export gates the menu off; folder
// export silently drops them).
const buildGpxElements = (artifact, p) => {
    const name = escapeXml(artifact.name);
    switch (p.kind) {
        case 'point':
            return `  <wpt lat="${p.coord.lat}" lon="${p.coord.lng}">
    <name>${name}</name>
  </wpt>`;
        case 'linestring': {
            const pts = p.coords
                .map(c => `      <trkpt lat="${c.lat}" lon="${c.lng}"></trkpt>`)
                .join('\n');
            return `  <trk>
    <name>${name}</name>
    <trkseg>
${pts}
    </trkseg>
  </trk>`;
        }
        case 'polygon':
            return null;
    }
};
const toGPX = (artifact, p) => {
    const el = buildGpxElements(artifact, p);
    if (el === null)
        return null;
    return `${GPX_HEADER}${el}
${GPX_FOOTER}`;
};
const triggerDownload = (filename, mime, content) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a tick so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
// Server-side formats trigger the ExportUserArtifact RPC and then navigate
// the browser to the returned presigned URL. Synchronous return shape is
// preserved by initiating the request and reporting success-on-dispatch;
// any backend error surfaces via the promise rejection and is logged.
const triggerServerExport = (artifact, format) => {
    const enumValue = format === 'shapefile' ? ExportFormat.EXPORT_FORMAT_SHAPEFILE
        : format === 'svg' ? ExportFormat.EXPORT_FORMAT_SVG
            : ExportFormat.EXPORT_FORMAT_DXF;
    artifactExportService
        .exportUserArtifact(artifact.id, enumValue)
        .then((result) => {
        if (!result.success || !result.downloadUrl) {
            console.error('User-artifact export rejected:', result.message);
            return;
        }
        // Navigating in a hidden anchor with the server-suggested filename
        // mirrors what triggerDownload does for client-side exports.
        const a = document.createElement('a');
        a.href = result.downloadUrl;
        if (result.filename)
            a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    })
        .catch((err) => {
        console.error('User-artifact export failed:', err);
    });
};
export const exportUserArtifact = (artifact, format) => {
    const p = readPrimitive(artifact);
    if (!p)
        return { ok: false, reason: 'unsupported or invalid geometry' };
    const base = sanitizeFilename(artifact.name);
    switch (format) {
        case 'geojson':
            triggerDownload(`${base}.geojson`, 'application/geo+json', toGeoJSON(artifact, p));
            return { ok: true };
        case 'gpx': {
            const gpx = toGPX(artifact, p);
            if (!gpx)
                return { ok: false, reason: 'GPX does not support polygon geometry' };
            triggerDownload(`${base}.gpx`, 'application/gpx+xml', gpx);
            return { ok: true };
        }
        case 'kml':
            triggerDownload(`${base}.kml`, 'application/vnd.google-earth.kml+xml', toKML(artifact, p));
            return { ok: true };
        case 'shapefile':
        case 'svg':
        case 'dxf':
            // GPX-style polygon check: SVG/DXF/Shapefile all support polygons, so
            // no per-primitive validation needed beyond the readPrimitive null
            // check above.
            triggerServerExport(artifact, format);
            return { ok: true };
        default:
            return { ok: false, reason: `unknown format: ${format}` };
    }
};
const flattenFolderNode = (node) => {
    const out = [...node.artifacts];
    for (const child of node.children)
        out.push(...flattenFolderNode(child));
    return out;
};
const folderToGeoJSON = (root) => {
    const features = flattenFolderNode(root)
        .map((a) => {
        const p = readPrimitive(a);
        return p ? buildGeoJSONFeature(a, p) : null;
    })
        .filter((f) => f !== null);
    return {
        content: JSON.stringify({ type: 'FeatureCollection', features }, null, 2),
        count: features.length,
    };
};
const folderToKML = (root) => {
    const styles = [];
    let count = 0;
    let seq = 0;
    const renderNode = (node) => {
        const placemarks = [];
        for (const a of node.artifacts) {
            const p = readPrimitive(a);
            if (!p)
                continue;
            const { style, placemark } = buildKmlPlacemark(a, p, `sty${seq++}`);
            styles.push(style);
            placemarks.push(placemark);
            count++;
        }
        const childXml = node.children.map(renderNode).join('');
        return `<Folder><name>${escapeXml(node.name)}</name>${placemarks.join('')}${childXml}</Folder>`;
    };
    const body = renderNode(root);
    const content = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(root.name)}</name>
    ${styles.join('')}
    ${body}
  </Document>
</kml>
`;
    return { content, count };
};
const folderToGPX = (root) => {
    const parts = [];
    for (const a of flattenFolderNode(root)) {
        const p = readPrimitive(a);
        if (!p)
            continue;
        const el = buildGpxElements(a, p);
        if (el === null)
            continue; // polygons have no GPX representation
        parts.push(el);
    }
    const content = `${GPX_HEADER}${parts.join('\n')}
${GPX_FOOTER}`;
    return { content, count: parts.length };
};
const triggerServerFolderExport = (folderId, format) => {
    const enumValue = format === 'shapefile' ? ExportFormat.EXPORT_FORMAT_SHAPEFILE
        : format === 'svg' ? ExportFormat.EXPORT_FORMAT_SVG
            : ExportFormat.EXPORT_FORMAT_DXF;
    artifactExportService
        .exportUserFolder(folderId, enumValue)
        .then((result) => {
        if (!result.success || !result.downloadUrl) {
            console.error('Folder export rejected:', result.message);
            return;
        }
        const a = document.createElement('a');
        a.href = result.downloadUrl;
        if (result.filename)
            a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    })
        .catch((err) => {
        console.error('Folder export failed:', err);
    });
};
export const exportFolder = (folderId, root, format) => {
    const base = sanitizeFilename(root.name);
    switch (format) {
        case 'geojson': {
            const { content, count } = folderToGeoJSON(root);
            if (count === 0)
                return { ok: false, reason: 'no exportable features in folder' };
            triggerDownload(`${base}.geojson`, 'application/geo+json', content);
            return { ok: true };
        }
        case 'kml': {
            const { content, count } = folderToKML(root);
            if (count === 0)
                return { ok: false, reason: 'no exportable features in folder' };
            triggerDownload(`${base}.kml`, 'application/vnd.google-earth.kml+xml', content);
            return { ok: true };
        }
        case 'gpx': {
            const { content, count } = folderToGPX(root);
            if (count === 0)
                return { ok: false, reason: 'no path or placemark features for GPX' };
            triggerDownload(`${base}.gpx`, 'application/gpx+xml', content);
            return { ok: true };
        }
        case 'shapefile':
        case 'svg':
        case 'dxf':
            triggerServerFolderExport(folderId, format);
            return { ok: true };
        default:
            return { ok: false, reason: `unknown format: ${format}` };
    }
};
